import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import "./GovernmentPortal.css";

const fmt = (d) => d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "-";
const money = (v) => `INR ${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const emptyUser = {
  fullName: "",
  mobile: "",
  email: "",
  password: "",
  officerCode: "",
  designation: "",
  officeName: "",
  district: "",
  area: "",
};

const emptySupplyOrder = {
  vendorId: "",
  productId: "",
  productType: "",
  quantity: "",
  unit: "KG",
  requirementAmount: "",
  locationText: "",
  dueDays: "30",
  notes: "",
};

const emptyGovVendorPayment = {
  governmentUserId: "",
  vendorId: "",
  amount: "",
  dueDate: "",
  area: "",
  instructions: "",
  beneficiaryNames: "",
};

const AdminGovernmentPortal = () => {
  const [users, setUsers] = useState([]);
  const [dispatches] = useState([]);
  const [supplierDispatches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products] = useState([]);
  const [vendorRequirements] = useState([]);
  const [govVendorPayments, setGovVendorPayments] = useState([]);
  const [form, setForm] = useState(emptyUser);
  const [supplyOrder, setSupplyOrder] = useState(emptySupplyOrder);
  const [govVendorPayment, setGovVendorPayment] = useState(emptyGovVendorPayment);
  const [assign, setAssign] = useState({ dispatchId: "", governmentUserId: "", area: "", farmerName: "", farmerMobile: "", amount: "", instructions: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [creatingUser, setCreatingUser] = useState(false);
  const [creatingSupplyOrder, setCreatingSupplyOrder] = useState(false);
  const [assigningGovVendor, setAssigningGovVendor] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const activeDesk = "thumb";
  const [editingUser, setEditingUser] = useState(null);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [editingAllotment, setEditingAllotment] = useState(null);
  const [updatingAllotment, setUpdatingAllotment] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [u, v, gp] = await Promise.all([
        API.get("/admin/government-users"),
        API.get("/admin/vendor-security"),
        API.get("/admin/government-vendor-payments"),
      ]);
      setUsers(Array.isArray(u.data) ? u.data : []);
      setVendors(Array.isArray(v.data) ? v.data : []);
      setGovVendorPayments(Array.isArray(gp.data) ? gp.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load government portal data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => ({
    users: users.length,
    assigned: govVendorPayments.length,
    paid: govVendorPayments.filter((row) => row.status === "paid").length,
  }), [users, govVendorPayments]);

  const vendorSupplyStats = useMemo(() => ({
    total: vendorRequirements.length,
    unpaid: vendorRequirements.filter((r) => !r.payment?.isPaid).length,
    paid: vendorRequirements.filter((r) => r.payment?.isPaid).length,
    receivable: vendorRequirements
      .filter((r) => !r.payment?.isPaid)
      .reduce((sum, r) => sum + Number(r.requirementAmount || 0), 0),
  }), [vendorRequirements]);

  const govVendorStats = useMemo(() => ({
    total: govVendorPayments.length,
    paid: govVendorPayments.filter((r) => r.status === "paid").length,
    remaining: govVendorPayments.reduce((sum, r) => sum + Number(r.remainingAmount || 0), 0),
  }), [govVendorPayments]);

  const recentVendorOrders = useMemo(
    () => [...vendorRequirements]
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      .slice(0, 12),
    [vendorRequirements]
  );

  const selectSupplyProduct = (productId) => {
    const product = products.find((p) => p._id === productId);
    setSupplyOrder((prev) => ({
      ...prev,
      productId,
      productType: product?.name || prev.productType,
      unit: product?.unit || prev.unit,
    }));
  };

  const createUser = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    setCreatedUser(null);
    setCreatingUser(true);
    try {
      const res = await API.post("/admin/government-users", form);
      setForm(emptyUser);
      setCreatedUser(res.data?.user || null);
      setMessage("Government user created. Share these login details with the officer.");
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create government user");
    } finally {
      setCreatingUser(false);
    }
  };

  const openUserEdit = (user) => setEditingUser({
    _id: user._id,
    fullName: user.fullName || "",
    mobile: user.mobile || "",
    email: user.email || "",
    password: "",
    officerCode: user.governmentProfile?.officerCode || "",
    designation: user.governmentProfile?.designation || "",
    officeName: user.governmentProfile?.officeName || "",
    district: user.governmentProfile?.district || "",
    area: user.governmentProfile?.area || "",
  });

  const updateGovernmentUser = async (e) => {
    e.preventDefault();
    if (!editingUser?._id) return;
    setMessage(""); setError(""); setUpdatingUser(true);
    try {
      const res = await API.put(`/admin/government-users/${editingUser._id}`, editingUser);
      setMessage(res.data?.message || "Government user updated.");
      setEditingUser(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Government user update failed");
    } finally {
      setUpdatingUser(false);
    }
  };

  const openAllotmentEdit = (row) => setEditingAllotment({
    _id: row._id,
    governmentUserId: row.governmentUser?._id || "",
    vendorId: row.vendor?._id || "",
    amount: row.amount || "",
    paidAmount: row.paidAmount || 0,
    dueDate: row.dueDate ? String(row.dueDate).slice(0, 10) : "",
    area: row.area || "",
    instructions: row.instructions || "",
    beneficiaryNames: (row.beneficiaryNames || []).join("\n"),
  });

  const updateAllotment = async (e) => {
    e.preventDefault();
    if (!editingAllotment?._id) return;
    setMessage(""); setError(""); setUpdatingAllotment(true);
    try {
      const res = await API.put(`/admin/government-vendor-payments/${editingAllotment._id}`, editingAllotment);
      setMessage(res.data?.message || "Thumb allotment updated.");
      setEditingAllotment(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Allotment update failed");
    } finally {
      setUpdatingAllotment(false);
    }
  };

  const assignDispatch = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!assign.dispatchId || !assign.governmentUserId) {
      setError("Select dispatch and government person.");
      return;
    }
    try {
      await API.patch(`/admin/supplier-dispatches/${assign.dispatchId}/government-assign`, assign);
      setAssign({ dispatchId: "", governmentUserId: "", area: "", farmerName: "", farmerMobile: "", amount: "", instructions: "" });
      setMessage("Dispatch assigned to government portal.");
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to assign dispatch");
    }
  };

  const createSupplyOrder = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!supplyOrder.vendorId) {
      setError("Select vendor first.");
      return;
    }
    try {
      setCreatingSupplyOrder(true);
      await API.post("/admin/vendor-supply-orders", supplyOrder);
      setSupplyOrder(emptySupplyOrder);
      setMessage("Vendor supply order created. Vendor can see it and pay from their portal.");
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create vendor supply order");
    } finally {
      setCreatingSupplyOrder(false);
    }
  };

  const assignVendorPaymentToGovernment = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    if (!govVendorPayment.governmentUserId || !govVendorPayment.vendorId) {
      setError("Select government person and vendor.");
      return;
    }
    try {
      setAssigningGovVendor(true);
      await API.post("/admin/government-vendor-payments", govVendorPayment);
      setGovVendorPayment(emptyGovVendorPayment);
      setMessage("Vendor payment assigned to government user.");
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to assign vendor payment");
    } finally {
      setAssigningGovVendor(false);
    }
  };

  return (
    <div className="gov-page admin-gov-page">
      <section className="gov-hero admin-gov-hero">
        <div>
          <span className="gov-kicker">Admin Control Center</span>
          <h1>Government Operations</h1>
          <p>Officer setup, Paddy security allotment, supplier dispatch aur payment tracking—har kaam ke liye alag, saaf desk.</p>
        </div>
        <div className="gov-stat-row">
          <div><strong>{stats.users}</strong><span>Users</span></div>
          <div><strong>{stats.assigned}</strong><span>Assigned</span></div>
          <div><strong>{stats.paid}</strong><span>Paid</span></div>
        </div>
      </section>

      {message && <div className="gov-success">{message}</div>}
      {error && <div className="gov-error">{error}</div>}

      {activeDesk === "setup-hidden" && <div className="admin-gov-desk-panel">
      <div className="admin-gov-context"><span className="material-symbols-outlined">info</span><div><b>Ye section kab use karein?</b><p>Pehle Government officer ka login banayein. Approved supplier dispatch aane par use sahi mandi officer ko assign karein.</p></div></div>
      <div className="admin-gov-grid">
        <form className="admin-gov-card" onSubmit={createUser}>
          <span className="admin-gov-card-tag">OFFICER LOGIN</span><h2>Create Government User</h2>
          <p className="admin-gov-card-help">Officer ke liye Government portal ka mobile/password login banega.</p>
          <div className="gov-form-grid">
            <input placeholder="Name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required />
            <input placeholder="Mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required />
            <input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input placeholder="Officer code" value={form.officerCode} onChange={(e) => setForm({ ...form, officerCode: e.target.value })} />
            <input placeholder="Designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            <input placeholder="Office" value={form.officeName} onChange={(e) => setForm({ ...form, officeName: e.target.value })} />
            <input placeholder="District" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} />
            <input className="gov-span-2" placeholder="Area / mandi / center" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} required />
          </div>
          <button className="gov-primary" type="submit" disabled={creatingUser}>
            {creatingUser ? "Creating..." : "Create User"}
          </button>
          {createdUser && (
            <div className="gov-created-user">
              <span>Login created for</span>
              <strong>{createdUser.fullName}</strong>
              <div>Mobile: <b>{createdUser.mobile}</b></div>
              <div>Password: <b>{createdUser.initialPassword || "Set by admin"}</b></div>
              <small>Portal: /government</small>
            </div>
          )}
        </form>

        <form className="admin-gov-card" onSubmit={assignDispatch}>
          <span className="admin-gov-card-tag">SUPPLIER → OFFICER</span><h2>Assign Supplier Dispatch</h2>
          <p className="admin-gov-card-help">Approved dispatch ko officer se jodiye, taaki farmer verification aur payment update ho sake.</p>
          <select value={assign.dispatchId} onChange={(e) => setAssign({ ...assign, dispatchId: e.target.value })}>
            <option value="">Select approved dispatch</option>
            {supplierDispatches.map((d) => (
              <option key={d._id} value={d._id}>
                {(d.companyName || d.supplierName || "Supplier")} - {d.materialName || "Material"} - {d.vehicle?.number || "No vehicle"}
              </option>
            ))}
          </select>
          <select value={assign.governmentUserId} onChange={(e) => {
            const user = users.find((u) => u._id === e.target.value);
            setAssign({ ...assign, governmentUserId: e.target.value, area: user?.governmentProfile?.area || assign.area });
          }}>
            <option value="">Select government person</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>{u.fullName} - {u.governmentProfile?.area || "Area"}</option>
            ))}
          </select>
          <input placeholder="Area" value={assign.area} onChange={(e) => setAssign({ ...assign, area: e.target.value })} />
          <input placeholder="Farmer name" value={assign.farmerName} onChange={(e) => setAssign({ ...assign, farmerName: e.target.value })} />
          <input placeholder="Farmer mobile" value={assign.farmerMobile} onChange={(e) => setAssign({ ...assign, farmerMobile: e.target.value })} />
          <input placeholder="Expected amount" type="number" value={assign.amount} onChange={(e) => setAssign({ ...assign, amount: e.target.value })} />
          <textarea placeholder="Instruction for supplier/government officer" rows={3} value={assign.instructions} onChange={(e) => setAssign({ ...assign, instructions: e.target.value })} />
          <button className="gov-primary" type="submit">Assign to Government</button>
        </form>
      </div>
      </div>}

      {activeDesk === "thumb" && <div className="admin-gov-desk-panel">
      <details className="admin-gov-create-user">
        <summary><span className="material-symbols-outlined">person_add</span><span><b>Add Government User</b><small>Naya mandi officer login banayein</small></span><span className="material-symbols-outlined admin-gov-expand">expand_more</span></summary>
        <form onSubmit={createUser}>
          <div className="admin-gov-context"><span className="material-symbols-outlined">info</span><div><b>Is account ka use kya hoga?</b><p>Officer mobile aur password se Government portal login karke assigned vendor ke Paddy thumb details submit karega.</p></div></div>
          <div className="gov-form-grid">
            <label className="gov-field"><span>Officer ka poora naam *</span><input placeholder="Jaise Amit Saxena" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} required /></label>
            <label className="gov-field"><span>Login mobile number *</span><input inputMode="numeric" placeholder="10–15 digit mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required /></label>
            <label className="gov-field"><span>Login password *</span><input placeholder="Minimum 6 characters" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></label>
            <label className="gov-field"><span>Email</span><input type="email" placeholder="Optional" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label className="gov-field"><span>Officer code</span><input placeholder="Jaise ETW102" value={form.officerCode} onChange={(e) => setForm({ ...form, officerCode: e.target.value })} /></label>
            <label className="gov-field"><span>Designation</span><input placeholder="Jaise MI" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></label>
            <label className="gov-field"><span>Office name</span><input placeholder="Office / center" value={form.officeName} onChange={(e) => setForm({ ...form, officeName: e.target.value })} /></label>
            <label className="gov-field"><span>District</span><input placeholder="District" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></label>
            <label className="gov-field gov-span-2"><span>Area / mandi / center *</span><input placeholder="Officer ka assigned area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} required /></label>
          </div>
          <button className="gov-primary" type="submit" disabled={creatingUser}><span className="material-symbols-outlined">person_add</span>{creatingUser ? "Creating..." : "Create Government User"}</button>
          {createdUser && <div className="gov-created-user"><span>Login successfully created</span><strong>{createdUser.fullName}</strong><div>Mobile: <b>{createdUser.mobile}</b></div><div>Password: <b>{createdUser.initialPassword || "Set by admin"}</b></div><small>Login URL: /government</small></div>}
        </form>
      </details>
      <details className="admin-gov-create-user admin-gov-existing-users">
        <summary><span className="material-symbols-outlined">manage_accounts</span><span><b>Existing Government Users</b><small>{users.length} users · details ya password edit karein</small></span><span className="material-symbols-outlined admin-gov-expand">expand_more</span></summary>
        <div className="admin-gov-user-list">
          {users.length === 0 ? <div className="gov-empty">Koi Government user nahi mila.</div> : users.map((user) => <article key={user._id} className="admin-gov-user-row">
            <span className="admin-gov-user-avatar">{String(user.fullName || "G").charAt(0).toUpperCase()}</span>
            <div><b>{user.fullName}</b><small>{user.mobile} · {user.governmentProfile?.designation || "Government officer"}</small><small>{user.governmentProfile?.area || "Area pending"} · {user.governmentProfile?.officeName || "Office pending"}</small></div>
            <button type="button" onClick={() => openUserEdit(user)}><span className="material-symbols-outlined">edit</span>Edit</button>
          </article>)}
        </div>
      </details>
      <div className="admin-gov-context highlight"><span className="material-symbols-outlined">grass</span><div><b>Paddy Thumb workflow</b><p>Security milne ke baad Vendor select karein → beneficiary names likhein → Government officer assign karein. Officer quantity dalega; amount ₹24.71/kg se automatic niklega.</p></div></div>
      <section className="admin-gov-card gov-supply-section">
        <div className="gov-section-head">
          <div>
            <span className="admin-gov-card-tag">SECURITY → PADDY THUMB</span><h2>Allot Vendor Paddy Thumb Limit</h2>
            <p>Vendor jo cash alag se jama kare, wahi amount yahan allot karein. Ye Vendor Panel Security se bilkul alag hai.</p>
          </div>
          <div className="gov-stat-row gov-stat-row-light">
            <div><strong>{govVendorStats.total}</strong><span>Assigned</span></div>
            <div><strong>{govVendorStats.paid}</strong><span>Paid</span></div>
            <div><strong>{money(govVendorStats.remaining)}</strong><span>Remaining</span></div>
          </div>
        </div>

        <form className="gov-supply-form" onSubmit={assignVendorPaymentToGovernment}>
          <select value={govVendorPayment.governmentUserId} onChange={(e) => {
            const user = users.find((u) => u._id === e.target.value);
            setGovVendorPayment({
              ...govVendorPayment,
              governmentUserId: e.target.value,
              area: user?.governmentProfile?.area || govVendorPayment.area,
            });
          }} required>
            <option value="">Select government person</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>{u.fullName} - {u.governmentProfile?.area || "Area"}</option>
            ))}
          </select>
          <select value={govVendorPayment.vendorId} onChange={(e) => setGovVendorPayment({ ...govVendorPayment, vendorId: e.target.value })} required>
            <option value="">Select vendor</option>
            {vendors.map((v) => (
              <option key={v.vendorId} value={v.vendorId}>{v.vendorName || v.companyName || "Vendor"} - {v.mobile || "No mobile"}</option>
            ))}
          </select>
          <input placeholder="Vendor se alag cash received / allotment amount" type="number" min="0" step="0.01" value={govVendorPayment.amount} onChange={(e) => setGovVendorPayment({ ...govVendorPayment, amount: e.target.value })} required />
          <input placeholder="Due date" type="date" value={govVendorPayment.dueDate} onChange={(e) => setGovVendorPayment({ ...govVendorPayment, dueDate: e.target.value })} />
          <input className="gov-span-2" placeholder="Area / mandi / center" value={govVendorPayment.area} onChange={(e) => setGovVendorPayment({ ...govVendorPayment, area: e.target.value })} />
          <textarea className="gov-span-2" placeholder="Instructions for government user" rows={3} value={govVendorPayment.instructions} onChange={(e) => setGovVendorPayment({ ...govVendorPayment, instructions: e.target.value })} />
          <textarea className="gov-span-2" placeholder={'Vendor ke beneficiary names — har line me ek\nJaise: Ram Kumar\nShyam Lal'} rows={4} value={govVendorPayment.beneficiaryNames} onChange={(e) => setGovVendorPayment({ ...govVendorPayment, beneficiaryNames: e.target.value })} />
          <div className="admin-gov-rate gov-span-2"><span className="material-symbols-outlined">calculate</span><div><small>FIXED PRODUCT & RATE</small><b>Paddy · ₹24.71 per kilogram</b><p>Example: 1,000 kg × ₹24.71 = ₹24,710</p></div></div>
          <button className="gov-primary" type="submit" disabled={assigningGovVendor}>
            {assigningGovVendor ? "Assigning..." : "Allot Thumb Impression"}
          </button>
        </form>

        <div className="admin-gov-table-wrap gov-supply-table">
          <table className="admin-gov-table">
            <thead>
              <tr>
                <th>Government User</th>
                <th>Vendor</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Used</th>
                <th>Remaining</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {govVendorPayments.length === 0 ? (
                <tr><td colSpan="8">No vendor payment assigned to government user yet.</td></tr>
              ) : govVendorPayments.map((r) => (
                <tr key={r._id}>
                  <td>{r.governmentUser?.fullName || "-"}<br /><small>{r.area || r.governmentUser?.governmentProfile?.area || ""}</small></td>
                  <td>{r.vendor?.companyName || r.vendor?.userId?.fullName || "Vendor"}<br /><small>{r.vendor?.userId?.mobile || r.vendor?.contactMobile || ""}</small></td>
                  <td><span className={`gov-pill ${r.status || "assigned"}`}>{r.status || "assigned"}</span></td>
                  <td>{money(r.amount)}</td>
                  <td>
                    {money(r.paidAmount)}
                    {(r.history || []).map((entry, index) => (
                      <small key={`${r._id}-thumb-${index}`} style={{ display: "block", marginTop: 4 }}>
                        {entry.productName || entry.personName || "-"}: {Number(entry.productQuantity ?? entry.quantity ?? 0)} {entry.unit || "unit"} product, {money(entry.amount)} — {entry.verificationStatus || (entry._id ? "submitted" : "legacy")}
                      </small>
                    ))}
                  </td>
                  <td>{money(r.remainingAmount)}</td>
                  <td>{fmt(r.lastPaidAt || r.updatedAt || r.createdAt)}</td>
                  <td><button className="admin-gov-edit-allotment" type="button" onClick={() => openAllotmentEdit(r)}><span className="material-symbols-outlined">edit</span>Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>}

      {editingUser && <div className="gov-modal-backdrop"><form className="gov-modal admin-gov-user-edit" onSubmit={updateGovernmentUser}>
        <div className="gov-modal-head"><div><span className="admin-gov-card-tag">EDIT OFFICER</span><h2>Government User Details</h2></div><button type="button" onClick={() => setEditingUser(null)}>×</button></div>
        <div className="gov-form-grid">
          <label className="gov-field"><span>Full name *</span><input value={editingUser.fullName} onChange={(e)=>setEditingUser({...editingUser,fullName:e.target.value})} required/></label>
          <label className="gov-field"><span>Mobile *</span><input inputMode="numeric" value={editingUser.mobile} onChange={(e)=>setEditingUser({...editingUser,mobile:e.target.value})} required/></label>
          <label className="gov-field"><span>Email</span><input type="email" value={editingUser.email} onChange={(e)=>setEditingUser({...editingUser,email:e.target.value})}/></label>
          <label className="gov-field"><span>New password</span><input type="password" placeholder="Blank = password change nahi hoga" value={editingUser.password} onChange={(e)=>setEditingUser({...editingUser,password:e.target.value})}/></label>
          <label className="gov-field"><span>Officer code</span><input value={editingUser.officerCode} onChange={(e)=>setEditingUser({...editingUser,officerCode:e.target.value})}/></label>
          <label className="gov-field"><span>Designation</span><input value={editingUser.designation} onChange={(e)=>setEditingUser({...editingUser,designation:e.target.value})}/></label>
          <label className="gov-field"><span>Office</span><input value={editingUser.officeName} onChange={(e)=>setEditingUser({...editingUser,officeName:e.target.value})}/></label>
          <label className="gov-field"><span>District</span><input value={editingUser.district} onChange={(e)=>setEditingUser({...editingUser,district:e.target.value})}/></label>
          <label className="gov-field gov-span-2"><span>Area / mandi / center</span><input value={editingUser.area} onChange={(e)=>setEditingUser({...editingUser,area:e.target.value})}/></label>
        </div>
        <button className="gov-primary" disabled={updatingUser}>{updatingUser ? "Updating..." : "Save Changes"}</button>
      </form></div>}

      {editingAllotment && <div className="gov-modal-backdrop"><form className="gov-modal admin-gov-user-edit" onSubmit={updateAllotment}>
        <div className="gov-modal-head"><div><span className="admin-gov-card-tag">EDIT ALLOTMENT</span><h2>Paddy Thumb Allotment</h2></div><button type="button" onClick={() => setEditingAllotment(null)}>×</button></div>
        <div className="admin-gov-rate"><span className="material-symbols-outlined">grass</span><div><small>FIXED RATE</small><b>Paddy · ₹24.71/kg</b><p>Already used: {money(editingAllotment.paidAmount)}</p></div></div>
        <div className="gov-form-grid">
          <label className="gov-field"><span>Government officer *</span><select value={editingAllotment.governmentUserId} onChange={(e)=>setEditingAllotment({...editingAllotment,governmentUserId:e.target.value})} required><option value="">Select officer</option>{users.map((u)=><option key={u._id} value={u._id}>{u.fullName} - {u.governmentProfile?.area || "Area"}</option>)}</select></label>
          <label className="gov-field"><span>Vendor *</span><select value={editingAllotment.vendorId} onChange={(e)=>setEditingAllotment({...editingAllotment,vendorId:e.target.value})} required><option value="">Select vendor</option>{vendors.map((v)=><option key={v.vendorId} value={v.vendorId}>{v.vendorName || v.companyName || "Vendor"}</option>)}</select></label>
          <label className="gov-field"><span>Cash received / allotment (₹) *</span><input type="number" min={Number(editingAllotment.paidAmount || 0)} step="0.01" value={editingAllotment.amount} onChange={(e)=>setEditingAllotment({...editingAllotment,amount:e.target.value})} required/><small>Vendor Panel Security se link nahi hai. Sirf already-used amount se kam nahi ho sakta.</small></label>
          <label className="gov-field"><span>Due date</span><input type="date" value={editingAllotment.dueDate} onChange={(e)=>setEditingAllotment({...editingAllotment,dueDate:e.target.value})}/></label>
          <label className="gov-field gov-span-2"><span>Area / mandi / center</span><input value={editingAllotment.area} onChange={(e)=>setEditingAllotment({...editingAllotment,area:e.target.value})}/></label>
          <label className="gov-field gov-span-2"><span>Instructions</span><textarea rows="3" value={editingAllotment.instructions} onChange={(e)=>setEditingAllotment({...editingAllotment,instructions:e.target.value})}/></label>
          <label className="gov-field gov-span-2"><span>Vendor beneficiary names</span><textarea rows="5" value={editingAllotment.beneficiaryNames} onChange={(e)=>setEditingAllotment({...editingAllotment,beneficiaryNames:e.target.value})} placeholder="Har line me ek naam"/></label>
        </div>
        <button className="gov-primary" disabled={updatingAllotment}>{updatingAllotment ? "Updating..." : "Save Allotment Changes"}</button>
      </form></div>}

      {activeDesk === "supply-hidden" && <div className="admin-gov-desk-panel">
      <div className="admin-gov-context"><span className="material-symbols-outlined">local_shipping</span><div><b>Ye Paddy security se alag workflow hai</b><p>Yahan company Vendor ko goods/order deti hai aur Vendor portal se uska payment collect hota hai.</p></div></div>
      <section className="admin-gov-card gov-supply-section">
        <div className="gov-section-head">
          <div>
            <h2>Vendor Supply & Payment</h2>
            <p>Vendor ko saman assign karein. Order vendor portal me requirement ke roop me dikhega aur payment wahi se collect hogi.</p>
          </div>
          <div className="gov-stat-row gov-stat-row-light">
            <div><strong>{vendorSupplyStats.total}</strong><span>Orders</span></div>
            <div><strong>{vendorSupplyStats.paid}</strong><span>Paid</span></div>
            <div><strong>{money(vendorSupplyStats.receivable)}</strong><span>Receivable</span></div>
          </div>
        </div>

        <form className="gov-supply-form" onSubmit={createSupplyOrder}>
          <select value={supplyOrder.vendorId} onChange={(e) => {
            const vendor = vendors.find((v) => v.vendorId === e.target.value);
            setSupplyOrder({
              ...supplyOrder,
              vendorId: e.target.value,
              locationText: vendor?.address || supplyOrder.locationText,
              dueDays: String(vendor?.paymentDueDays ?? supplyOrder.dueDays),
            });
          }} required>
            <option value="">Select vendor</option>
            {vendors.map((v) => (
              <option key={v.vendorId} value={v.vendorId}>
                {v.vendorName || v.companyName || "Vendor"} - Limit {money(v.availableLimit)}
              </option>
            ))}
          </select>
          <select value={supplyOrder.productId} onChange={(e) => selectSupplyProduct(e.target.value)}>
            <option value="">Select product (optional)</option>
            {products.map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
          <input placeholder="Product / saman name" value={supplyOrder.productType} onChange={(e) => setSupplyOrder({ ...supplyOrder, productType: e.target.value })} required />
          <input placeholder="Quantity" type="number" min="0" step="0.01" value={supplyOrder.quantity} onChange={(e) => setSupplyOrder({ ...supplyOrder, quantity: e.target.value })} required />
          <select value={supplyOrder.unit} onChange={(e) => setSupplyOrder({ ...supplyOrder, unit: e.target.value })}>
            <option value="KG">KG</option>
            <option value="Ton">Ton</option>
            <option value="Quintal">Quintal</option>
            <option value="Litre">Litre</option>
          </select>
          <input placeholder="Amount vendor will pay" type="number" min="0" step="0.01" value={supplyOrder.requirementAmount} onChange={(e) => setSupplyOrder({ ...supplyOrder, requirementAmount: e.target.value })} required />
          <input placeholder="Payment due days" type="number" min="0" value={supplyOrder.dueDays} onChange={(e) => setSupplyOrder({ ...supplyOrder, dueDays: e.target.value })} />
          <input className="gov-span-2" placeholder="Delivery address / area" value={supplyOrder.locationText} onChange={(e) => setSupplyOrder({ ...supplyOrder, locationText: e.target.value })} required />
          <textarea className="gov-span-2" placeholder="Instructions / notes" rows={3} value={supplyOrder.notes} onChange={(e) => setSupplyOrder({ ...supplyOrder, notes: e.target.value })} />
          <button className="gov-primary" type="submit" disabled={creatingSupplyOrder}>
            {creatingSupplyOrder ? "Assigning..." : "Assign Goods to Vendor"}
          </button>
        </form>

        <div className="admin-gov-table-wrap gov-supply-table">
          <table className="admin-gov-table">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Goods</th>
                <th>Dispatch</th>
                <th>Payment</th>
                <th>Amount</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {recentVendorOrders.length === 0 ? (
                <tr><td colSpan="6">No vendor supply order yet.</td></tr>
              ) : recentVendorOrders.map((r) => (
                <tr key={r._id}>
                  <td>{r.vendor?.companyName || r.vendor?.fullName || "Vendor"}<br /><small>{r.vendor?.mobile || ""}</small></td>
                  <td>{r.productType || "-"}<br /><small>{Number(r.quantity || 0)} {r.unit || ""}</small></td>
                  <td><span className={`gov-pill ${r.dispatch?.status || "pending"}`}>{r.dispatch?.status || "pending"}</span></td>
                  <td><span className={`gov-pill ${r.payment?.isPaid ? "paid" : "assigned"}`}>{r.payment?.isPaid ? "paid" : (r.payment?.latestStatus || "unpaid")}</span></td>
                  <td>{money(r.requirementAmount)}</td>
                  <td>{fmt(r.creditTerms?.dueDate || r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </div>}

      {activeDesk === "tracking-hidden" && <div className="admin-gov-desk-panel">
      <div className="admin-gov-context"><span className="material-symbols-outlined">monitoring</span><div><b>Read-only tracking</b><p>Supplier, officer, farmer, status aur amount ka consolidated result yahan dekhein.</p></div></div>
      <section className="admin-gov-card">
        <span className="admin-gov-card-tag">LIVE STATUS</span><h2>Assigned Dispatch Payment Status</h2>
        {loading ? (
          <div className="gov-empty">Loading...</div>
        ) : dispatches.length === 0 ? (
          <div className="gov-empty">No government dispatch assignment yet.</div>
        ) : (
          <div className="admin-gov-table-wrap">
            <table className="admin-gov-table">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th>Dispatch</th>
                  <th>Government Person</th>
                  <th>Farmer</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {dispatches.map((d) => (
                  <tr key={d._id}>
                    <td>{d.companyName || d.supplierName || "-"}</td>
                    <td>{d.materialName || "-"}<br /><small>{d.vehicle?.number || "-"}</small></td>
                    <td>{d.governmentPayment?.assignedTo?.fullName || "-"}<br /><small>{d.governmentPayment?.area || "-"}</small></td>
                    <td>{d.governmentPayment?.farmerName || "-"}<br /><small>{d.governmentPayment?.farmerMobile || ""}</small></td>
                    <td><span className={`gov-pill ${d.governmentPayment?.status || "assigned"}`}>{d.governmentPayment?.status || "assigned"}</span></td>
                    <td>{money(d.governmentPayment?.amount)}</td>
                    <td>{fmt(d.governmentPayment?.updatedAt || d.governmentPayment?.assignedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      </div>}
    </div>
  );
};

export default AdminGovernmentPortal;
